import { useMemo, useState } from 'react';

import { skipToken } from '@apollo/client/react';
import {
  OdProtosPartnershipData_PartnershipType as PartnershipType,
  OdProtosPartnershipData_ReferralInvitationAcceptanceStatus as ReferralAcceptanceStatus,
  OdProtosPartnershipData_Referral_AttributionType as ReferralAttributionType,
} from '__generated__/globalTypes';
import type {
  PartnershipReferralsQuery_partnership_OdProtosPartnershipServices_Partnership_Query_getReferralsForSellerInput_OdProtosPartnershipServices_GetReferralsForSellerInputResponse_referrals_OdProtosPartnershipData_Referral as Referral,
  PartnershipReferralsQuery_partnership_OdProtosPartnershipServices_Partnership_Query_getReferralInvitation_OdProtosPartnershipServices_GetReferralInvitationResponse_invitation_OdProtosPartnershipData_ReferralInvitation as ReferralInvitation,
} from 'growth/__generated__/usePartnershipReferralStep';
import {
  PartnershipReferralsDocument,
  ReferralInvitationEligibilityDocument,
} from 'growth/__generated__/usePartnershipReferralStep';
import { useQuery } from 'platform/apollo';
import { offerPollingTimeoutThreshold } from 'shared/helpers/globals';
import { useBrowserCompatibilityData } from 'shared/helpers/utils';
import { useTimeout } from 'shared/hooks/useTimeout';

/** Agent refer/represent choice or agent referral form (unknown attribution on the agency referral). */
export type AgentReferralStep = {
  type: 'agent-referral';
  agentReferral: Referral;
  referralInvitiationPaymentEligibility: boolean;
};

/** Seller accepts or declines a pending referral invitation. */
export type CustomerReferralAcceptanceStep = {
  type: 'customer-referral-acceptance';
  referralInvitation: ReferralInvitation;
  referralInvitiationPaymentEligibility: boolean;
};

export type ReferralStep = AgentReferralStep | CustomerReferralAcceptanceStep;

/**
 * Extracts and determines if the referral step is required.
 *
 * Previous comment:
 *     Channel check unnecessary because we would have had updated the
 *     attribution type if the agent opted for representation. If an ops
 *     user manually flipped to lead from agent -> sell direct, the
 *     agent referral would be deleted.
 *     The referralInvitiationEligibility criterion takes into account
 *     the listed status of the lead. Listed leads are not eligible.
 */
function extractPartnershipReferralStep(
  agentReferral: Referral | undefined,
  referralInvitiationEligibility: boolean,
  referralInvitation: ReferralInvitation | null,
  referralInvitiationPaymentEligibility: boolean,
  isOpendoorAppEvoWebView: boolean,
): ReferralStep | undefined {
  // AgentReferralStep is skipped for the Opendoor App Evo Web View
  // Todo: Validate with T&O/Product if we can start showing it
  if (isOpendoorAppEvoWebView) {
    return undefined;
  }

  // This represents that we need the agent to chose whether to refer or represent the customer
  const hasUnknownAttribution =
    agentReferral?.attributionType === ReferralAttributionType.UNKNOWN_ATTRIBUTION &&
    referralInvitiationEligibility;

  // REFERRAL means the agent referred; reverting sell-direct → agent flips attribution to REPRESENTATION.
  const isPendingAcceptance =
    agentReferral?.attributionType === ReferralAttributionType.REFERRAL &&
    referralInvitation?.status === ReferralAcceptanceStatus.PENDING;

  if (isPendingAcceptance && referralInvitation) {
    return {
      type: 'customer-referral-acceptance',
      referralInvitation,
      referralInvitiationPaymentEligibility,
    };
  }
  if (hasUnknownAttribution && agentReferral) {
    return {
      type: 'agent-referral',
      agentReferral,
      referralInvitiationPaymentEligibility,
    };
  }

  return undefined;
}

interface PartnershipReferralResponse {
  loading: boolean;
  partnershipReferralStep: ReferralStep | undefined;
}

const ORPHANED_PARTNERSHIP_TYPES = [
  PartnershipType.CORPORATE_BENEFITS,
  PartnershipType.INDEPENDENT_DEVELOPER,
  PartnershipType.REIT,
  PartnershipType.MARKETING,
];

// REAL_ESTATE_AGENCY get the "Agent" Experience
const AGENT_PARTNERSHIP_TYPES = [PartnershipType.REAL_ESTATE_AGENCY];

/** Returns the partnership referral step a customer must complete, if any */
export const usePartnershipReferralStep = (
  sellerInputUUID?: string,
): PartnershipReferralResponse => {
  const { isOpendoorAppEvoWebView } = useBrowserCompatibilityData();

  const referralQuery = useQuery(
    PartnershipReferralsDocument,
    !sellerInputUUID?.trim()
      ? skipToken
      : {
          variables: { sellerInputUUID },
          fetchPolicy: 'cache-first',
          context: { linkContext: { team: 'growth' } },
        },
  );

  const referralInvitationQuery = useQuery(
    ReferralInvitationEligibilityDocument,
    !sellerInputUUID?.trim()
      ? skipToken
      : {
          variables: { sellerInputUUID },
          pollInterval: 800,
          context: { linkContext: { team: 'growth' } },
        },
  );

  const [timeoutElapsed, setTimeoutElapsed] = useState(false);
  useTimeout(() => setTimeoutElapsed(true), offerPollingTimeoutThreshold);

  const shouldStopPolling =
    referralInvitationQuery.data?.partnership.getReferralInvitationEligibility?.eligibilityReady ||
    timeoutElapsed;

  if (shouldStopPolling) {
    referralInvitationQuery.stopPolling();
  }

  const loading = referralQuery.loading || referralInvitationQuery.loading;

  const referralQueryData = referralQuery.data;
  const referralInvitationData = referralInvitationQuery.data;

  return useMemo<PartnershipReferralResponse>(() => {
    const referralInvitiationEligibility =
      referralInvitationData?.partnership.getReferralInvitationEligibility?.eligible || false;
    const referralInvitation =
      referralQueryData?.partnership.getReferralInvitation?.invitation || null;
    const referralInvitiationPaymentEligibility =
      referralInvitationData?.partnership.getReferralInvitationEligibility?.paymentEligible ||
      false;

    // Filter ORPHANED_PARTNERSHIP_TYPES out from response and set referral attributes based on response data
    const allReferrals = referralQueryData?.partnership.getReferralsForSellerInput?.referrals || [];

    let agentReferral: Referral | undefined = undefined;
    const partnershipReferrals: Referral[] = [];
    for (const referral of allReferrals) {
      if (ORPHANED_PARTNERSHIP_TYPES.includes(referral.partnershipType)) {
        continue;
      }
      if (AGENT_PARTNERSHIP_TYPES.includes(referral.partnershipType)) {
        agentReferral = referral;
      }
      partnershipReferrals.push(referral);
    }

    const partnershipReferralStep = extractPartnershipReferralStep(
      agentReferral,
      referralInvitiationEligibility,
      referralInvitation,
      referralInvitiationPaymentEligibility,
      isOpendoorAppEvoWebView,
    );

    return {
      loading,
      partnershipReferralStep,
    };
  }, [isOpendoorAppEvoWebView, loading, referralQueryData, referralInvitationData]);
};
